import React, { useState } from 'react';
import logo from '../assets/logo.jpg';

export default function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [adminName, setAdminName] = useState('');

  const handleSendOTP = async () => {
    if (!username.trim()) { setError('Please enter your username'); return; }
    if (!password.trim()) { setError('Please enter your password'); return; }
    setLoading(true); setError(''); setInfo('');
    try {
      const res = await fetch('/api/send-otp', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'send', username:username.trim(), password }),
      });
      const data = await res.json();
      if (data.success) { setAdminName(data.name||username); setInfo(data.message||'OTP sent.'); setStep(2); setPassword(''); }
      else { setError(data.error||'Invalid username or password.'); }
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()||otp.length!==6) { setError('Please enter the 6-digit OTP'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/send-otp', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'verify', otp }),
      });
      const data = await res.json();
      if (data.success) { onLogin(adminName); }
      else { setError(data.error||'Wrong OTP. Try again.'); setOtp(''); }
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight:'100vh',
      background:'radial-gradient(ellipse at 30% 40%, #0d2252 0%, #070f22 50%, #03060f 100%)',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:20, position:'relative', overflow:'hidden',
      fontFamily:"'Raleway',sans-serif",
    }}>
      {/* ── WATERMARK — large, clear, centred ── */}
      <div style={{
        position:'absolute', inset:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        pointerEvents:'none', zIndex:0,
      }}>
        <img src={logo} alt="" style={{
          width:'min(600px, 90vw)', height:'min(600px, 90vw)',
          objectFit:'contain',
          opacity:0.18,
          filter:'saturate(0) brightness(1.6)',
        }}/>
      </div>

      {/* ── CORNER GOLD ACCENTS ── */}
      <div style={{ position:'absolute', top:0, left:0, width:180, height:180, pointerEvents:'none', zIndex:1 }}>
        <svg width="180" height="180" viewBox="0 0 180 180" fill="none">
          <path d="M0 0 L180 0" stroke="rgba(201,168,76,0.3)" strokeWidth="1"/>
          <path d="M0 0 L0 180" stroke="rgba(201,168,76,0.3)" strokeWidth="1"/>
          <path d="M0 0 L60 0 L60 4 L4 4 L4 60 L0 60 Z" fill="rgba(201,168,76,0.25)"/>
        </svg>
      </div>
      <div style={{ position:'absolute', bottom:0, right:0, width:180, height:180, pointerEvents:'none', zIndex:1 }}>
        <svg width="180" height="180" viewBox="0 0 180 180" fill="none">
          <path d="M180 180 L0 180" stroke="rgba(201,168,76,0.3)" strokeWidth="1"/>
          <path d="M180 180 L180 0" stroke="rgba(201,168,76,0.3)" strokeWidth="1"/>
          <path d="M180 180 L120 180 L120 176 L176 176 L176 120 L180 120 Z" fill="rgba(201,168,76,0.25)"/>
        </svg>
      </div>

      {/* ── MAIN CONTENT — no card box ── */}
      <div style={{ position:'relative', zIndex:2, width:'100%', maxWidth:440, textAlign:'center' }}>

        {/* Church branding */}
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:'0.65rem', letterSpacing:'0.45em', color:'rgba(201,168,76,0.6)', textTransform:'uppercase', marginBottom:14 }}>
            ✦ Secure Admin Panel ✦
          </div>
          <div style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'1rem', color:'#c9a84c', lineHeight:1.3, marginBottom:4 }}>
            ZION ASSEMBLIES OF GOD
          </div>
          <div style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'0.7rem', color:'rgba(201,168,76,0.55)', letterSpacing:'0.2em' }}>
            CHURCH — OOTY
          </div>
        </div>

        {/* Gold divider */}
        <div style={{ display:'flex', alignItems:'center', gap:12, margin:'22px 0' }}>
          <div style={{ flex:1, height:1, background:'linear-gradient(to right, transparent, rgba(201,168,76,0.5))' }} />
          <div style={{ width:6, height:6, background:'#c9a84c', transform:'rotate(45deg)' }} />
          <div style={{ flex:1, height:1, background:'linear-gradient(to left, transparent, rgba(201,168,76,0.5))' }} />
        </div>

        {/* Step dots */}
        <div style={{ display:'flex', justifyContent:'center', gap:10, marginBottom:28 }}>
          {[1,2].map(n=>(
            <div key={n} style={{
              width: step>n ? 24 : step===n ? 28 : 8, height:8, borderRadius:4,
              background: step>n ? '#5cb85c' : step===n ? '#c9a84c' : 'rgba(201,168,76,0.2)',
              transition:'all 0.3s',
            }}/>
          ))}
        </div>

        {/* STEP 1 */}
        {step===1 && (<>
          <div style={{ fontSize:'2.6rem', marginBottom:10 }}>🔐</div>
          <div style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'0.88rem', color:'#f0e8d8', marginBottom:6 }}>Step 1 of 2 — Login</div>
          <div style={{ fontFamily:"'Crimson Pro',serif", fontStyle:'italic', fontSize:'0.9rem', color:'rgba(200,191,168,0.7)', marginBottom:24 }}>
            Enter your username and password
          </div>

          {error && (
            <div style={{ background:'rgba(178,34,34,0.2)', border:'1px solid rgba(178,34,34,0.5)', borderRadius:10, padding:'11px 16px', marginBottom:14, color:'#ff9a9a', fontSize:'0.82rem' }}>
              ❌ {error}
            </div>
          )}

          <div style={{ marginBottom:14, textAlign:'left' }}>
            <div style={{ fontSize:'0.64rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'#c9a84c', marginBottom:7, fontWeight:700 }}>Username</div>
            <input type="text" placeholder="e.g. Philip Rajkumar"
              value={username} onChange={e=>setUsername(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleSendOTP()}
              autoFocus
              style={{
                width:'100%', padding:'14px 18px',
                background:'rgba(255,255,255,0.06)',
                border:'1px solid rgba(201,168,76,0.3)',
                borderRadius:12, color:'#f0e8d8',
                fontSize:'0.95rem', outline:'none',
                fontFamily:"'Raleway',sans-serif",
                transition:'border-color 0.2s, background 0.2s',
              }}
              onFocus={e=>{ e.target.style.borderColor='rgba(201,168,76,0.7)'; e.target.style.background='rgba(255,255,255,0.1)'; }}
              onBlur={e=>{ e.target.style.borderColor='rgba(201,168,76,0.3)'; e.target.style.background='rgba(255,255,255,0.06)'; }}
            />
          </div>

          <div style={{ marginBottom:22, textAlign:'left' }}>
            <div style={{ fontSize:'0.64rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'#c9a84c', marginBottom:7, fontWeight:700 }}>Password</div>
            <input type="password" placeholder="Enter your password"
              value={password} onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleSendOTP()}
              style={{
                width:'100%', padding:'14px 18px',
                background:'rgba(255,255,255,0.06)',
                border:'1px solid rgba(201,168,76,0.3)',
                borderRadius:12, color:'#f0e8d8',
                fontSize:'0.95rem', outline:'none',
                fontFamily:"'Raleway',sans-serif",
              }}
              onFocus={e=>{ e.target.style.borderColor='rgba(201,168,76,0.7)'; e.target.style.background='rgba(255,255,255,0.1)'; }}
              onBlur={e=>{ e.target.style.borderColor='rgba(201,168,76,0.3)'; e.target.style.background='rgba(255,255,255,0.06)'; }}
            />
          </div>

          <button onClick={handleSendOTP} disabled={loading} style={{
            width:'100%', padding:'15px',
            background: loading ? 'rgba(201,168,76,0.4)' : 'linear-gradient(135deg,#c9a84c 0%,#a07020 50%,#c9a84c 100%)',
            backgroundSize:'200% 100%',
            border:'none', borderRadius:12, color:'#070f22',
            fontFamily:"'Raleway',sans-serif", fontWeight:800,
            fontSize:'0.88rem', letterSpacing:'0.15em', textTransform:'uppercase',
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : '0 8px 30px rgba(201,168,76,0.35)',
          }}>
            {loading ? '⏳ Sending OTP…' : 'Send OTP to My Email  →'}
          </button>
        </>)}

        {/* STEP 2 */}
        {step===2 && (<>
          <div style={{ fontSize:'2.6rem', marginBottom:10 }}>📧</div>
          <div style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'0.88rem', color:'#f0e8d8', marginBottom:6 }}>Step 2 of 2 — Verify OTP</div>
          <div style={{ fontFamily:"'Crimson Pro',serif", fontStyle:'italic', fontSize:'0.9rem', color:'rgba(200,191,168,0.7)', marginBottom:20 }}>
            Hi <strong style={{color:'#c9a84c'}}>{adminName}</strong>! Check your email.
          </div>

          {info && (
            <div style={{ background:'rgba(92,184,92,0.12)', border:'1px solid rgba(92,184,92,0.4)', borderRadius:10, padding:'11px 16px', marginBottom:14, color:'#80e87a', fontSize:'0.82rem' }}>
              ✅ {info}
            </div>
          )}
          {error && (
            <div style={{ background:'rgba(178,34,34,0.2)', border:'1px solid rgba(178,34,34,0.5)', borderRadius:10, padding:'11px 16px', marginBottom:14, color:'#ff9a9a', fontSize:'0.82rem' }}>
              ❌ {error}
            </div>
          )}

          <div style={{ marginBottom:22, textAlign:'left' }}>
            <div style={{ fontSize:'0.64rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'#c9a84c', marginBottom:7, fontWeight:700 }}>6-Digit OTP</div>
            <input type="text" inputMode="numeric" maxLength={6}
              placeholder="● ● ● ● ● ●"
              value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,''))}
              onKeyDown={e=>e.key==='Enter'&&handleVerifyOTP()}
              autoFocus
              style={{
                width:'100%', padding:'16px 18px',
                background:'rgba(255,255,255,0.06)',
                border:'1px solid rgba(201,168,76,0.3)',
                borderRadius:12, color:'#f0e8d8',
                fontSize:'2rem', letterSpacing:'0.5em', textAlign:'center',
                outline:'none', fontFamily:"'Raleway',sans-serif",
              }}
              onFocus={e=>{ e.target.style.borderColor='rgba(201,168,76,0.7)'; e.target.style.background='rgba(255,255,255,0.1)'; }}
              onBlur={e=>{ e.target.style.borderColor='rgba(201,168,76,0.3)'; e.target.style.background='rgba(255,255,255,0.06)'; }}
            />
          </div>

          <button onClick={handleVerifyOTP} disabled={loading} style={{
            width:'100%', padding:'15px',
            background: loading ? 'rgba(201,168,76,0.4)' : 'linear-gradient(135deg,#c9a84c,#8b6914)',
            border:'none', borderRadius:12, color:'#070f22',
            fontFamily:"'Raleway',sans-serif", fontWeight:800,
            fontSize:'0.88rem', letterSpacing:'0.15em', textTransform:'uppercase',
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : '0 8px 30px rgba(201,168,76,0.35)',
            marginBottom:14,
          }}>
            {loading ? '⏳ Verifying…' : '✅ Verify & Login'}
          </button>

          <div style={{ display:'flex', justifyContent:'center', gap:24 }}>
            <button onClick={()=>{setStep(1);setOtp('');setError('');setInfo('');}} style={{ background:'none', border:'none', color:'rgba(201,168,76,0.5)', cursor:'pointer', fontSize:'0.76rem', letterSpacing:'0.08em', fontFamily:"'Raleway',sans-serif" }}>
              ← Back
            </button>
            <button onClick={handleSendOTP} disabled={loading} style={{ background:'none', border:'none', color:'rgba(201,168,76,0.7)', cursor:'pointer', fontSize:'0.76rem', letterSpacing:'0.08em', fontFamily:"'Raleway',sans-serif" }}>
              🔁 Resend OTP
            </button>
          </div>
        </>)}

        {/* Footer */}
        <div style={{ marginTop:32, fontSize:'0.6rem', color:'rgba(201,168,76,0.2)', letterSpacing:'0.15em', textTransform:'uppercase' }}>
          Zion Assemblies of God Church, Ooty · Admin Access Only ✝
        </div>

      </div>
    </div>
  );
}
